import _ from "lodash";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { accSearchFormUpdate } from "../../actions/search/accommodation_search";
import { startSearch } from "../../actions/single_search/accommodation_search";
import { validateNewHotelSearch } from "../../components/MainSearchForm/hotel/helpers";
import FullScreenLoader from "../../components/widgets/FullScreenLoader";
import { hotelsErrorMsgs } from "../../errors";
import { useApoAuth } from "../../hooks";
import jssVariables from "../../jss_setup";
import { StyleTypeContext } from "../../umbilical_tools";

function recreateFormData(decoded_data) {
  const destination_b64_hash = decoded_data.query.id[0];
  let destination_code = window.atob(destination_b64_hash);

  // Convert "lv1__" through "lv5__" to "lvl1__" through "lvl5__"
  destination_code = destination_code.replace(/lv([1-5])__/g, "lvl$1___");

  // Default dates: checkIn 7 days from now, checkOut 2 days after checkIn
  const defaultCheckIn = DateTime.now().plus({ days: 7 }).toISODate();
  const defaultCheckOut = DateTime.now().plus({ days: 9 }).toISODate();

  const data = {
    rooms: decoded_data.rooms,
    adults: decoded_data.rooms.reduce((a, b) => a + b.adults, 0),
    children: decoded_data.rooms.reduce((a, b) => a + b.children, 0),
    children_ages: _.flatten(decoded_data.rooms.map((r) => r.children_ages)),
    checkIn: decoded_data.check_in || defaultCheckIn,
    checkOut: decoded_data.check_out || defaultCheckOut,
    destination_code: [destination_code],
    destination_meta: { location_type: "destination", uid: destination_code },
  };

  return data;
}

const styles = createUseStyles({
  DeepHotelSearch: (props) => ({
    position: "absolute",
    background: "#FAFAFA",
    width: "100vw",
    height: "100vh",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepHotelSearch", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
});
const DeepHotelSearch = ({ encoded_data }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepHotelSearch",
  });

  const classes = styles({ customStyles });

  function onError() {
    toast.warning(hotelsErrorMsgs.searchForm.init_search, { duration: 5000 });
    history.push("/");
  }

  if (!encoded_data) {
    onError();
  }

  var decoded_data = {};
  try {
    const jsoned = JSON.parse(window.atob(encoded_data));
    decoded_data = recreateFormData(jsoned);
  } catch {
    onError();
  }

  const onFormUpdate = useCallback(
    ({ form }) => {
      dispatch(accSearchFormUpdate({ form }));
    },
    [dispatch],
  );

  const { source_entity, market_type } = useApoAuth();
  const onFormSearch = useCallback(() => {
    dispatch(startSearch({ redirect: true, history, source_entity, market_type }));
  }, [dispatch]);

  async function onSearch() {
    await onFormUpdate({
      form: {
        ...decoded_data,
        acc_search_type: "dest",
      },
    });
    onFormSearch();
  }

  // Execute search
  useEffect(() => {
    if (!source_entity) {
      return;
    }

    if (_.isEmpty(decoded_data)) {
      return;
    }

    if (
      !validateNewHotelSearch({
        destinationObj: { destination_code: decoded_data.destination_code },
        datesArray: [decoded_data.checkIn, decoded_data.checkOut],
      })
    ) {
      onError();
      return;
    }

    onSearch();
  }, [encoded_data, source_entity]);

  return (
    <div className={classes.DeepHotelSearch}>
      <FullScreenLoader />
    </div>
  );
};
DeepHotelSearch.propTypes = {
  encoded_data: PropTypes.string.isRequired,
};
export default DeepHotelSearch;
